import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { REASONS } from "../lib/constants";
import { FileUploader } from "../components/FileUploader";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

// Each row gets an internal client-side id so React lists stay stable across
// add/remove without using array index as the key. Not sent to the API.
let __rowSeq = 0;
const newItem = () => ({
  __rid: `i${++__rowSeq}-${Date.now()}`,
  item_name: "", item_code: "", hsn_code: "", quantity: 1, unit: "Nos", rate: 0, gst_percent: 18,
});

export default function ReturnNewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    customer_id: "",
    customer_name: "",
    customer_code: "",
    contact_person: "",
    mobile: "",
    email: "",
    items: [newItem()],
    reason: "",
    complaint_description: "",
    transporter_name: "",
    lr_number: "",
    dispatch_date: "",
    vehicle_number: "",
    attachments: [],
  });

  useEffect(() => {
    api.get("/customers")
      .then((r) => setCustomers(r.data))
      .catch(() => {
        // Non-fatal: customer list stays empty; the UI will show "Pick a customer"
        // placeholder. Failures surface elsewhere via the global toast on submit.
      });
  }, []);

  // Auto-fill customer for customer role. We intentionally read form.customer_id
  // inside the setter callback so this effect doesn't need to re-run on every
  // form keystroke.
  useEffect(() => {
    if (user?.role !== "customer" || !customers.length) return;
    const c = customers.find((x) => x.id === user.customer_id) || customers[0];
    if (!c) return;
    setForm((f) => {
      if (f.customer_id) return f; // already filled
      return {
        ...f,
        customer_id: c.id,
        customer_name: c.name,
        customer_code: c.customer_code,
        contact_person: c.contact_person || "",
        mobile: c.mobile || "",
        email: c.email || "",
      };
    });
  }, [user, customers]);

  const setItem = (i, patch) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)),
    }));
  };

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, newItem()] }));
  const delItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const totals = form.items.reduce((acc, it) => {
    const v = (it.quantity || 0) * (it.rate || 0);
    acc.value += v;
    acc.tax += v * (it.gst_percent || 0) / 100;
    return acc;
  }, { value: 0, tax: 0 });

  const submit = async (status) => {
    if (!form.customer_name) return toast.error("Customer is required");
    if (!form.reason) return toast.error("Reason is required");
    if (form.items.length === 0 || !form.items[0].item_name) return toast.error("Add at least one item");
    setSubmitting(true);
    try {
      // Strip the client-side row id (__rid) — it's only used for stable React keys.
      const cleanItems = form.items.map(({ __rid, ...rest }) => rest);
      const payload = { ...form, items: cleanItems, status };
      const { data } = await api.post("/returns", payload);
      toast.success(`Saved as ${status}. Request No: ${data.request_number}`);
      navigate(`/returns/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="return-new-page">
      <div>
        <h1 className="heading text-3xl font-bold text-slate-900">New Return Request</h1>
        <p className="text-slate-500 mt-1">Fill in product, return, dispatch and attachment details.</p>
      </div>

      {/* Customer section */}
      <Card>
        <CardHeader><CardTitle className="heading text-base">Customer Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {user?.role !== "customer" && (
            <div className="md:col-span-2">
              <Label>Customer</Label>
              <Select value={form.customer_id} onValueChange={(v) => {
                const c = customers.find((x) => x.id === v);
                setForm((f) => ({ ...f, customer_id: v, customer_name: c?.name || "", customer_code: c?.customer_code || "",
                  contact_person: c?.contact_person || "", mobile: c?.mobile || "", email: c?.email || "" }));
              }}>
                <SelectTrigger data-testid="select-customer"><SelectValue placeholder="Pick a customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.customer_code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div><Label>Customer Name</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="input-customer-name" /></div>
          <div><Label>Customer Code</Label><Input value={form.customer_code} onChange={(e) => setForm({ ...form, customer_code: e.target.value })} /></div>
          <div><Label>Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
          <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
          <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        </CardContent>
      </Card>

      {/* Items */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="heading text-base">Product Details</CardTitle>
          <Button variant="outline" size="sm" onClick={addItem} data-testid="add-item-button"><Plus className="w-4 h-4 mr-1" /> Add item</Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {form.items.map((it, i) => (
            <div key={it.__rid} className="grid grid-cols-12 gap-3 items-end border border-slate-200 rounded p-3 bg-slate-50/50" data-testid={`item-row-${i}`}>
              <div className="col-span-5">
                <Label className="text-xs">Item Name</Label>
                <Input value={it.item_name} onChange={(e) => setItem(i, { item_name: e.target.value })} placeholder="Type item name" data-testid={`item-name-${i}`} />
              </div>
              <div className="col-span-1"><Label className="text-xs">HSN</Label><Input value={it.hsn_code} onChange={(e) => setItem(i, { hsn_code: e.target.value })} /></div>
              <div className="col-span-1"><Label className="text-xs">Qty</Label><Input type="number" min="0" value={it.quantity} onChange={(e) => setItem(i, { quantity: parseFloat(e.target.value) || 0 })} data-testid={`item-qty-${i}`} /></div>
              <div className="col-span-1"><Label className="text-xs">Unit</Label><Input value={it.unit} onChange={(e) => setItem(i, { unit: e.target.value })} /></div>
              <div className="col-span-1"><Label className="text-xs">Rate</Label><Input type="number" min="0" value={it.rate} onChange={(e) => setItem(i, { rate: parseFloat(e.target.value) || 0 })} /></div>
              <div className="col-span-2"><Label className="text-xs">GST%</Label><Input type="number" min="0" value={it.gst_percent} onChange={(e) => setItem(i, { gst_percent: parseFloat(e.target.value) || 0 })} /></div>
              <div className="col-span-1 text-right">
                <Button variant="ghost" size="icon" onClick={() => delItem(i)} disabled={form.items.length === 1} data-testid={`item-remove-${i}`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
          <div className="flex justify-end gap-6 text-sm">
            <div><span className="text-slate-500">Sub Total: </span><span className="font-medium">₹ {totals.value.toFixed(2)}</span></div>
            <div><span className="text-slate-500">GST: </span><span className="font-medium">₹ {totals.tax.toFixed(2)}</span></div>
            <div><span className="text-slate-500">Total: </span><span className="font-semibold text-slate-900">₹ {(totals.value + totals.tax).toFixed(2)}</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Return + Dispatch */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="heading text-base">Return Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Reason for Return</Label>
              <Input
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="e.g. Damaged in transit, Quality defect, Wrong specification..."
                data-testid="input-reason"
              />
            </div>
            <div>
              <Label>Complaint Description</Label>
              <Textarea rows={4} value={form.complaint_description} onChange={(e) => setForm({ ...form, complaint_description: e.target.value })} data-testid="input-complaint" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="heading text-base">Dispatch Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Transporter</Label>
              <Input
                value={form.transporter_name}
                onChange={(e) => setForm({ ...form, transporter_name: e.target.value })}
                placeholder="Type transporter name"
                data-testid="input-transporter"
              />
            </div>
            <div><Label>LR / Bilty No</Label><Input value={form.lr_number} onChange={(e) => setForm({ ...form, lr_number: e.target.value })} data-testid="input-lr" /></div>
            <div><Label>Dispatch Date</Label><Input type="date" value={form.dispatch_date} onChange={(e) => setForm({ ...form, dispatch_date: e.target.value })} /></div>
            <div className="col-span-2"><Label>Vehicle No</Label><Input value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })} /></div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="heading text-base">Attachments</CardTitle></CardHeader>
        <CardContent>
          <FileUploader value={form.attachments} onChange={(v) => setForm({ ...form, attachments: v })} label="LR / Invoice / Photos (PDF, JPG, PNG)" testId="new-return-uploader" />
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3">
        <Button variant="outline" disabled={submitting} onClick={() => submit("Draft")} data-testid="save-draft-button">Save Draft</Button>
        <Button className="bg-orange-600 hover:bg-orange-700" disabled={submitting} onClick={() => submit("Submitted")} data-testid="submit-request-button">
          Submit Request
        </Button>
      </div>
    </div>
  );
}
